<template>
  <!-- Provider cost (DeepSeek) — extracted from ConfiguracoesIaSettingsForm by the
       M4a split. Every elem-id and data-testid is byte-identical to the pre-split
       markup (specs and the credit-surface inventory key off them).
       What IS new, deliberately: the heading and the provider-cost banner.
       markup / peg_brl / fx_rate_usd_brl were dormant since M3 and removed
       from the console in M6b-2 (contract no longer declares them). -->
  <div class="provider-cost-form">
    <div
      v-if="pricingLoaded"
      class="settings-section"
      data-testid="config-ia-burn-rate-form"
    >
    <h4 class="section-title">{{ t('Custo do provedor (DeepSeek)') }}</h4>
    <p class="provider-cost-banner" data-testid="provider-cost-banner">
      {{ t('Preços de tabela do provedor. Copie da página de preços do DeepSeek — não é uma decisão de precificação.') }}
    </p>
    <div class="form-fields">
      <input-number
        v-model="rateForm.cache_hit_usd_per_1m"
        elem-id="burn-rate-rate-input"
        :label="t('Taxa cache_hit USD/1M tokens')"
        data-testid="burn-rate-rate-input"
        :min-fraction-digits="2"
        :max-fraction-digits="8"
      />
      <input-number
        v-model="rateForm.cache_miss_usd_per_1m"
        elem-id="burn-rate-cache-miss"
        :label="t('Taxa cache_miss USD/1M tokens')"
        data-testid="burn-rate-cache-miss-input"
        :min-fraction-digits="2"
        :max-fraction-digits="8"
      />
      <input-number
        v-model="rateForm.output_usd_per_1m"
        elem-id="burn-rate-output"
        :label="t('Taxa output USD/1M tokens')"
        data-testid="burn-rate-output-input"
        :min-fraction-digits="2"
        :max-fraction-digits="8"
      />
    </div>
    <div class="form-actions">
      <Button
        :label="t('Publicar nova taxa')"
        icon="pi pi-upload"
        severity="primary"
        :loading="publishingRate"
        data-testid="burn-rate-publish-button"
        @click="handlePublishRate"
      />
    </div>
    </div>

    <global-peg-form />
    <reserve-settings-form />
    <peg-overrides-table />
    <pricing-history-table />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { useAiSettings } from '../../../composables/useAiSettings'
import { emitValidationFailed } from '../../../composables/useFormValidation'
import { log } from '../../../utils/DebugLogger'

const { t } = useI18n()

const emit = defineEmits<{
  (e: 'validation-failed'): void
}>()

const {
  currentPricing,
  publishingRate,
  loadCurrentPricing,
  publishPricingRate,
} = useAiSettings()

// ── Local form state ─────────────────────────────────────────────────────────

const pricingLoaded = ref(false)

interface RateForm {
  cache_hit_usd_per_1m: number | null
  cache_miss_usd_per_1m: number | null
  output_usd_per_1m: number | null
}

const rateForm = reactive<RateForm>({
  cache_hit_usd_per_1m: null,
  cache_miss_usd_per_1m: null,
  output_usd_per_1m: null,
})

// ── Validation ───────────────────────────────────────────────────────────────

function validateRateForm(): boolean {
  const errors: Record<string, string> = {}
  if (rateForm.cache_hit_usd_per_1m === null) {
    errors.cache_hit_usd_per_1m = t('Taxa cache_hit é obrigatória')
  }
  if (rateForm.cache_miss_usd_per_1m === null) {
    errors.cache_miss_usd_per_1m = t('Taxa cache_miss é obrigatória')
  }
  if (rateForm.output_usd_per_1m === null) {
    errors.output_usd_per_1m = t('Taxa output é obrigatória')
  }
  if (Object.values(errors).some(Boolean)) {
    emitValidationFailed('ProviderCostForm', errors)
    emit('validation-failed')
    return false
  }
  return true
}

// ── Handlers ─────────────────────────────────────────────────────────────────

async function handlePublishRate(): Promise<void> {
  if (!validateRateForm()) return
  try {
    // valid_from = now in ISO format (YYYY-MM-DD HH:MM:SS)
    const now = new Date()
    const valid_from = now.toISOString().replace('T', ' ').slice(0, 19)
    // No franquia_id: provider cost is GLOBAL (2026-08-21). It is what the
    // provider charges us — a fact about the model — and overriding it per
    // franchise falsified COGS and margin wherever those columns were read.
    // The per-franchise lever is the PEG; see PegOverridesTable below.
    await publishPricingRate({
      model_name: 'deepseek-v4-flash',
      cache_hit_usd_per_1m: String(rateForm.cache_hit_usd_per_1m),
      cache_miss_usd_per_1m: String(rateForm.cache_miss_usd_per_1m),
      output_usd_per_1m: String(rateForm.output_usd_per_1m),
      valid_from,
    })
    eventBus.emit('globalMessage.success', t('Nova taxa publicada com sucesso'))
  } catch (err) {
    log('ProviderCostForm', 'handlePublishRate', 'Erro ao publicar taxa', err)
    eventBus.emit('globalMessage.error', t('Erro ao publicar taxa'))
  }
}

// ── Lifecycle ─────────────────────────────────────────────────────────────────

onMounted(async () => {
  // Load current pricing — degrade gracefully on failure
  try {
    await loadCurrentPricing()
    if (currentPricing.value) {
      rateForm.cache_hit_usd_per_1m = parseFloat(currentPricing.value.cache_hit_usd_per_1m) || null
      rateForm.cache_miss_usd_per_1m = parseFloat(currentPricing.value.cache_miss_usd_per_1m) || null
      rateForm.output_usd_per_1m = parseFloat(currentPricing.value.output_usd_per_1m) || null
    }
    pricingLoaded.value = true
  } catch (err) {
    log('ProviderCostForm', 'mounted', 'Pricing indisponível', err)
  }
})
</script>

<style scoped>
.settings-section {
  background: var(--p-surface-0, #fff);
  border: 1px solid var(--p-surface-200, #e5e7eb);
  border-radius: 8px;
  padding: 20px;
}

.section-title {
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--p-text-color);
}

.form-fields {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
}

.form-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.provider-cost-banner {
  margin: -8px 0 16px 0;
  font-size: 13px;
  color: var(--p-text-muted-color, #6b7280);
}
</style>
